import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import HeroChatBotBox from '../components/ui/HeroChatBotBox'
import StatsSection from '../components/ui/StatsSection'
import { ShopCard } from '../components/ui/cards'
import type { TyreFitmentCentre } from '../data/tyreFitmentCentres'
import { useLocation, calculateDistance } from '../hooks/useLocation'
import { supabase } from '../services/supabase'
import SuggestFitmentCentreModal from '../components/ui/modals/SuggestFitmentCentreModal'

import MobileBottomBar from '../components/layout/MobileBottomBar';

const HomePage: React.FC = () => {
  const navigate = useNavigate()
  const routerLocation = useRouterLocation()
  const { isAuthenticated } = useAuthStore()

  // Scroll to the embedded chat whenever the user arrives via /#chat
  // (Sign in / Get Started buttons, or any old /login link)
  useEffect(() => {
    if (routerLocation.hash === '#chat') {
      // Small delay so the hero section has rendered before we scroll
      setTimeout(() => {
        document.getElementById('chat')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
  }, [routerLocation.hash, routerLocation.key])

  const { location, loading, requestLocation, hasPermission } = useLocation()
  const [nearbyShops, setNearbyShops] = useState<TyreFitmentCentre[]>([])
  const [showSuggestModal, setShowSuggestModal] = useState(false)

  const [locationName, setLocationName] = useState<string>('')
  const [dataLoading, setDataLoading] = useState(true)


  // Fetch nearby businesses from Supabase
  const fetchNearbyBusinesses = async (userLocation?: { latitude: number; longitude: number }) => {
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('*')
        .eq('verified', true);

      if (error) {
        console.error('Error fetching businesses:', error);
        return;
      }

      if (!data || data.length === 0) {
        console.log('No businesses found in database');
        setNearbyShops([]);
        return;
      }

      // Transform data with safe access and type assertion
      let transformedShops: TyreFitmentCentre[] = data.map((business: any) => ({
        id: business.id || '',
        name: business.name || 'Tyre Shop',
        address: business.address || '',
        city: business.city || 'Johannesburg',
        province: business.province || 'Gauteng',
        phone: business.phone || business.contact_phone || '',
        email: business.email || business.contact_email || '',
        website: business.website || '',
        coordinates: business.coordinates || { lat: -26.1076, lng: 28.0567 },
        rating: business.rating || 4.5,
        reviewCount: business.review_count || 0,
        services: business.services || ['Tyre Fitting'],
        openingHours: business.business_hours || {
          'Monday': '8:00 AM - 7:00 PM',
          'Tuesday': '8:00 AM - 7:00 PM',
          'Wednesday': '8:00 AM - 7:00 PM',
          'Thursday': '8:00 AM - 7:00 PM',
          'Friday': '8:00 AM - 7:00 PM',
          'Saturday': '8:00 AM - 4:00 PM',
          'Sunday': '9:00 AM - 2:00 PM'
        },
        specialties: business.specialties || ['Professional Service'],
        logo_url: business.logo_url || '',
        imageUrl: business.logo_url || '', // Add missing imageUrl property
        is_online: business.is_online || false,
        website: business.website_url || business.website || '',
        slug: business.slug || '',
        distance: undefined,
        estimatedTime: undefined
      }));

      // Filter by distance if location is provided. Online stores have no
      // physical location, so they are kept without a distance (never filtered
      // out by proximity) and are sorted after the nearby physical shops.
      if (userLocation) {
        transformedShops = transformedShops.filter(shop => {
          if (shop.is_online) {
            shop.distance = undefined;
            return true;
          }
          const distance = calculateDistance(
            userLocation.latitude,
            userLocation.longitude,
            shop.coordinates.lat,
            shop.coordinates.lng
          );
          shop.distance = distance;
          return distance <= 100; // Within 100km
        }).sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
      }

      setNearbyShops(transformedShops.slice(0, 3));
    } catch (error) {
      console.error('Error fetching nearby businesses:', error);
      setNearbyShops([]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setDataLoading(true);
      // Only fetch businesses if location is enabled
      if (hasPermission) {
        await fetchNearbyBusinesses(location ? { latitude: location.latitude, longitude: location.longitude } : undefined);
      } else {
        setNearbyShops([]);
      }
      setDataLoading(false);
    };

    loadData();

    // Set location name based on user location or default
    if (location && location.city) {
      setLocationName(location.city);
    } else if (location) {
      setLocationName('your area');
    } else if (hasPermission) {
      setLocationName('your area'); // Location permission granted but still loading
    } else {
      setLocationName('your area'); // Default when no location available
    }
  }, [location, hasPermission])



  const handleViewShop = (shop: TyreFitmentCentre) => {
    // Navigate to shop details (replace with actual shop route)
    navigate('/fitment-centres', {
      state: { selectedShop: shop.id }
    })
  }

  const handleCallShop = (shop: TyreFitmentCentre) => {
    window.open(`tel:${shop.phone}`, '_self')
  }

  const handleGetDirections = (shop: TyreFitmentCentre) => {
    const { lat, lng } = shop.coordinates
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
    window.open(url, '_blank')
  }

  const handleLocationRequest = () => {
    requestLocation()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white">
        {/* Tyre warehouse background image */}
        <img
          src="https://img.freepik.com/free-photo/dark-storage-full-big-variety-new-tyres-busy-warehouse_613910-3839.jpg?semt=ais_items_boosted&w=740"
          alt="Tyre warehouse"
          className="w-full h-full object-cover absolute inset-0 z-0"
          style={{ objectPosition: "center" }}
        />
        {/* Blue overlay with 70% opacity */}
        <div className="absolute inset-0 z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom right, rgba(37, 99, 235, 0.7), rgba(29, 78, 216, 0.7), rgba(30, 64, 175, 0.7))" }} />
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            {/* Location-aware heading */}
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Find Perfect Tyres{location && location.city ? ` in ${locationName}` : ''}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Find local stock, compare prices, connect with local tyre experts
            </p>

            {/* Location permission request */}
            {hasPermission === false && (
              <div className="mb-8 bg-blue-800 bg-opacity-50 rounded-lg p-4 max-w-md mx-auto">
                <p className="text-blue-100 mb-3">
                  📍 Enable location to find nearby tyre shops and better deals
                </p>
                <button
                  onClick={handleLocationRequest}
                  className="bg-black text-white px-6 py-2 rounded-lg font-medium hover:bg-neutral-900 transition-colors"
                >
                  Enable Location
                </button>
              </div>
            )}

            {/* Loading state */}
            {loading && (
              <div className="mb-8">
                <p className="text-blue-100">🔍 Finding your location...</p>
              </div>
            )}

            {/* Chat Assistant Section */}
            <div id="chat" className="scroll-mt-20">
              <HeroChatBotBox />
            </div>
          </div>
        </div>
      </div>

      {/* Nearest Shops Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              📍 Tyre Shops Near {locationName}
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              Professional installation services in your area
            </p>
            {hasPermission === false && (
              <div className="mb-6">
                <button
                  onClick={handleLocationRequest}
                  className="bg-black text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  📍 Find Shops Near Me
                </button>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dataLoading ? (
              // Loading skeleton for shops
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 animate-pulse">
                  <div className="h-32 bg-gray-200 rounded-lg mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))
            ) : hasPermission === false ? (
              // Location disabled - Enhanced prominent design
              <div className="col-span-full">
                <div className="max-w-2xl mx-auto">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-8 shadow-lg">
                    <div className="text-center">
                      {/* Animated icon */}
                      <div className="relative mb-6">
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
                          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        {/* Subtle glow effect */}
                        <div className="absolute inset-0 w-20 h-20 bg-blue-400 rounded-full mx-auto opacity-10 blur-md"></div>
                      </div>

                      {/* Main heading */}
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        📍 Location Access Required
                      </h3>

                      {/* Subtext */}
                      <p className="text-lg text-gray-700 mb-2">
                        Enable location services to discover nearby tyre fitment centres.
                      </p>
                      <p className="text-gray-600 mb-8 font-medium">
                        Find professional installation services in your area instantly.
                      </p>

                      {/* Prominent CTA button */}
                      <button
                        onClick={handleLocationRequest}
                        className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        🔓 Enable Location Access
                      </button>

                      {/* Additional info */}
                      <div className="mt-6 pt-6 border-t border-blue-200">
                        <p className="text-sm text-gray-500">
                          Your location helps us show you the most relevant tyre services nearby.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : nearbyShops.length > 0 ? (
              nearbyShops.map((shop) => (
                <ShopCard
                  key={shop.id}
                  shop={shop}
                  onViewShop={handleViewShop}
                  onCallShop={handleCallShop}
                  onGetDirections={handleGetDirections}
                />
              ))
            ) : (
              // Location enabled but no shops within 100km - Enhanced prominent design
              <div className="col-span-full">
                <div className="max-w-2xl mx-auto">
                  <div className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200 rounded-2xl p-8 shadow-lg">
                    <div className="text-center">
                      {/* Animated icon */}
                      <div className="relative mb-6">
                        <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center mx-auto shadow-lg animate-pulse">
                          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        {/* Pulsing ring effect */}
                        <div className="absolute inset-0 w-20 h-20 bg-orange-400 rounded-full mx-auto animate-ping opacity-20"></div>
                      </div>

                      {/* Main heading */}
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        🚨 No Fitment Centres Found in Your Area
                      </h3>

                      {/* Subtext */}
                      <p className="text-lg text-gray-700 mb-2">
                        We couldn't find any verified tyre shops within 100km of your location.
                      </p>
                      <p className="text-gray-600 mb-8 font-medium">
                        Help us expand our network! Suggest a fitment centre near you.
                      </p>

                      {/* Prominent CTA button */}
                      <button
                        onClick={() => setShowSuggestModal(true)}
                        className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        💡 Suggest a Fitment Centre
                      </button>

                      {/* Additional info */}
                      <div className="mt-6 pt-6 border-t border-orange-200">
                        <p className="text-sm text-gray-500">
                          Your suggestion helps us bring tyre services to more South African communities.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/fitment-centres')}
              className="bg-black text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              View All Tyre Shops
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Use TyreLink?
            </h2>
            <p className="text-xl text-gray-600">
              The smart way to find and buy tyres
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Easy Search */}
            <div className="text-center">
              <div className="bg-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Easy Search</h3>
              <p className="text-gray-600">
                Find tyres by vehicle details or direct size. Get instant compatibility checks.
              </p>
            </div>

            {/* Vetted Shops */}
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Vetted Shops</h3>
              <p className="text-gray-600">
                Every shop on TyreLink is checked by our team before it goes live, so you deal with real, reliable tyre businesses.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <StatsSection />

      {/* Enhanced CTA Section */}
      {!isAuthenticated && (
        <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-20">
          <div className="max-w-6xl mx-auto text-center px-4">
            <div className="mb-8">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Save More on Every Tyre Purchase
              </h2>
              <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto">
                Find the right tyres and mags at real, vetted shops near you — quickly and for free.
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">👨‍🔧</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">Expert Installation Services</h3>
                <p className="text-blue-100">Professional fitment by local experts</p>
              </div>
              <div className="text-center">
                <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">📍</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">Find Local Shops</h3>
                <p className="text-blue-100">Get directions to shops nearby</p>
                <p className="text-blue-100">Connect with verified tyre professionals</p>
              </div>
              <div className="text-center">
                <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">⚡</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">Quick Service</h3>
                <p className="text-blue-100">Find what you need in under 2 minutes</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="bg-white text-blue-600 px-10 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg transform hover:scale-105"
              >
                Find Tyres Now
              </button>
            </div>


            {/* Trust indicators */}
            <div className="mt-8 text-sm text-blue-200">
              <p>✓ Free to use ✓ Real, vetted shops ✓ Local fitment ✓ Help on WhatsApp</p>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Bar */}
      <MobileBottomBar variant="home" />

      {/* Suggest Fitment Centre Modal */}
      <SuggestFitmentCentreModal
        isOpen={showSuggestModal}
        onClose={() => setShowSuggestModal(false)}
        userLocation={location ? {
          lat: location.latitude,
          lng: location.longitude,
          city: location.city
        } : undefined}
      />

    </div>
  );
}

export default HomePage